// Sprint PKG-VIZ A1 (#792) — shared <PKGCanvas /> contract.
//
// Consumed by every PKG-VIZ UI surface (A2, A3, B2, C2, D1, D2): the
// in-project neighbourhood panel, the full explorer page, the partner
// portfolio canvas, and the agent session trace. This is the durable,
// additive-only export surface — see Sprint-Artefacts/Sprint-A1-impl-prompt.md
// §5 (S2) before renaming or removing anything here.
//
// PKGNodeEntityType / PKGEdgeType mirror the real Postgres enums in
// apps/api/src/db/schema/pkg.ts (pkg_node_entity_type / pkg_edge_type)
// verbatim. They are duplicated here rather than imported because
// packages/shared must not depend on apps/api — keep the two lists in
// sync by hand if the schema enum ever changes.

export const PKG_NODE_ENTITY_TYPES = [
  // Table-row entities
  'artefact',
  'decision',
  'risk',
  'issue',
  'meeting',
  // Label entities — distinct nodes from their row-entity counterparts
  'requirement',
  'user_story',
  'adr_label',
  'risk_label',
  'issue_label',
] as const;
export type PKGNodeEntityType = typeof PKG_NODE_ENTITY_TYPES[number];

export const PKG_EDGE_TYPES = [
  'EVIDENCE_LINK',
  'DECISION_OF',
  'RISK_OF',
  'IMPLEMENTS',
  'DERIVED_FROM',
  'REFERENCES',
] as const;
export type PKGEdgeType = typeof PKG_EDGE_TYPES[number];

export type LayoutHint = 'radial' | 'dagre' | 'force';

// 'editable' is part of the contract now (D1 consumes it) but A1 renders
// it as a stub — no drag/reconnect wiring. See adjudication Q5.
export type InteractionLevel = 'readonly' | 'interactive' | 'editable';

export interface PKGNode {
  id: string;
  entityType: PKGNodeEntityType;
  entityId: string;
  summary: string | null;
  /**
   * Optional supplementary display text (status/severity/date/etc.).
   * Not a pkg_nodes column — the raw substrate only has id/entityType/
   * entityId/summary. Populated by whichever fetch layer (A2/B1) joins
   * against the underlying decisions/risks/requirements/... tables.
   */
  badge?: string;
}

export interface PKGEdge {
  id: string;
  /** Matches React Flow's edge.source — the origin pkg_nodes.id. */
  source: string;
  /** Matches React Flow's edge.target — the destination pkg_nodes.id. */
  target: string;
  edgeType: PKGEdgeType;
  createdByAi: boolean;
}

export interface PKGCanvasProps {
  nodes: PKGNode[];
  edges: PKGEdge[];
  layoutHint: LayoutHint;
  interactionLevel: InteractionLevel;
  /**
   * Which node `layoutHint: 'radial'` centers on. Optional and additive
   * (R2 F8) — defaults to the lexicographically-first node id when
   * omitted, matching A1's original behavior. Ignored by `dagre`/`force`.
   */
  centerNodeId?: string;
  /**
   * Fired on node click.
   *
   * R2 (F3): deliberately not instantaneous. A real browser double-click
   * fires native `click` twice before `dblclick` once, so PKGCanvas
   * defers this callback by a short discrimination window (~200-300ms)
   * — long enough for a following double-click to cancel it, so
   * onNodeClick never fires alongside onNodeExpand. A consumer wiring
   * onNodeClick to something latency-sensitive (e.g. re-centering the
   * canvas on the clicked node) will see that ~200-300ms delay on every
   * single click. This is the double-click/single-click disambiguation
   * cost, not a bug — do not "fix" it by calling onNodeClick eagerly.
   */
  onNodeClick?: (node: PKGNode) => void;
  onEdgeClick?: (edge: PKGEdge) => void;
  /**
   * Fired on node double-click. Signal-only — no fetch, no promise; the
   * caller decides what "expand" means (adjudication Q7).
   *
   * PKGCanvas sets `zoomOnDoubleClick={false}` on the underlying
   * ReactFlow instance so double-click is exclusively this signal, not
   * React Flow's default "zoom the canvas" gesture. This is part of the
   * contract, not an implementation detail: any consumer wiring its own
   * double-click behavior on nodes rendered by PKGCanvas should route
   * through this prop rather than re-attaching a competing handler.
   *
   * R2 (F3): "exclusively" is enforced by PKGCanvas debouncing the
   * single-click callback so a following native double-click cancels it
   * — a real browser double-click fires `click` twice before `dblclick`,
   * so without this, `onNodeClick` would also fire.
   *
   * R2 (F10): the `zoomOnDoubleClick={false}` fix is only needed because
   * `nodesDraggable={false}` strips a node's internal React-Flow-owned
   * 'nopan' class. If a future story (D1) makes `nodesDraggable`
   * conditional on `interactionLevel === 'editable'`, re-verify this
   * interaction for editable mode — enabling drag restores 'nopan',
   * which is what currently keeps React Flow's pane-level zoom-on-
   * double-click handler from competing for the event in the first place.
   */
  onNodeExpand?: (node: PKGNode) => void;
  /**
   * D1 (#801) — fired when the user clicks the confirm/reject pill
   * rendered on an AI-inferred edge (`edge.createdByAi === true`) while
   * `interactionLevel === 'editable'`. Additive (Q1/L1): absent on every
   * consumer that predates this story (A2/B2/A3), no pills render and no
   * behavior changes for them. Signal-only, same as `onEdgeClick`/
   * `onNodeExpand` — PKGCanvas does not call the confirm/reject endpoint
   * itself; the caller owns the fetch and any optimistic-update/refetch
   * strategy.
   */
  onEdgeConfirm?: (edge: PKGEdge) => void;
  /** D1 (#801) — see `onEdgeConfirm`. Fired on the reject pill. */
  onEdgeReject?: (edge: PKGEdge) => void;
  /**
   * D2 (#802) — fired when the user completes a drag-to-connect gesture
   * between two node handles while `interactionLevel === 'editable'`
   * (React Flow's native `onConnect`, forwarded as-is). Additive, same
   * shape as `onEdgeConfirm`/`onEdgeReject`: absent on every consumer
   * that predates this story, no connection handles render and no
   * behavior changes for them. Signal-only — PKGCanvas does not call
   * `POST .../pkg/edges` itself; the caller owns the edge-type-picker
   * UI and the request. `fromNodeId`/`toNodeId` are the two nodes'
   * `PKGNode.id` values, not yet validated against the real edge-type
   * matrix (that's server-side, see services/pkg/edge-manual-add.ts) —
   * this callback fires on ANY completed drag, valid pairing or not.
   */
  onEdgeAdd?: (connection: { fromNodeId: string; toNodeId: string }) => void;
}
